/** Keep session reads from observing the gap while a password change rotates its cookie. */
export function createSessionFetch(fetchImpl: typeof fetch): typeof fetch {
  const rotations = new Set<Promise<void>>();
  let revision = 0;

  return async (input, init) => {
    const request = input instanceof Request ? input : undefined;
    const path = new URL(request?.url ?? String(input), "https://auth.invalid").pathname;
    const method = init?.method ?? request?.method ?? "GET";
    const signal = init?.signal ?? request?.signal;
    if (path.endsWith("/change-password") && method.toUpperCase() === "POST") {
      let finish!: () => void;
      const rotation = new Promise<void>((resolve) => {
        finish = resolve;
      });
      rotations.add(rotation);
      revision += 1;
      try {
        // fetch resolves after the browser has received the replacement cookie.
        return await fetchImpl(input, init);
      } finally {
        rotations.delete(rotation);
        finish();
      }
    }
    if (!path.endsWith("/get-session")) return fetchImpl(input, init);

    for (;;) {
      await waitForRotations(Promise.all([...rotations]), signal);
      const startedAt = revision;
      const response = await fetchImpl(input, init);
      if (startedAt === revision) return response;
      // A read already in flight may have used the retired cookie. Read again
      // with the replacement instead of briefly signing the UI out.
      await response.body?.cancel();
    }
  };
}

async function waitForRotations(pending: Promise<unknown>, signal?: AbortSignal | null) {
  signal?.throwIfAborted();
  if (!signal) return pending;
  let abort!: () => void;
  try {
    await Promise.race([
      pending,
      new Promise<never>((_, reject) => {
        abort = () => reject(signal.reason);
        signal.addEventListener("abort", abort, { once: true });
      }),
    ]);
  } finally {
    signal.removeEventListener("abort", abort);
  }
}
